import React, { useState } from 'react';
import { getFirestore, collection, addDoc, Timestamp, getDocs, writeBatch, doc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { sendNotification } from '../../services/oneSignalService';

// Composants UI
import Modal from '../ui/Modal/Modal';
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import TextArea from '../ui/TextArea/TextArea';
import Select from '../ui/Select/Select';
import DateCheckbox from '../ui/DateCheckbox/DateCheckbox';
import MultiSelect from '../ui/MultiSelect/MultiSelect';
import './CreateDealForm.css';

const CreateDealForm = ({ onClose, onSuccess }) => {
  // États
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    urlRedirection: '',
    dateDebut: '',
    dateFin: '',
    conditions: '',
    type: 'online'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasDateLimit, setHasDateLimit] = useState(false);
  const [sendNotif, setSendNotif] = useState(true);

  // Options pour le type de bon plan
  const typeOptions = [
    { value: 'online', label: 'En ligne' },
    { value: 'store', label: 'En magasin' },
    { value: 'both', label: 'En ligne et en magasin' },
    { value: 'announcement', label: 'Annonce' }
  ];

  // Gestionnaires d'événements
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'titre' && value.length > 25) {
      return; // Ne pas mettre à jour si plus de 25 caractères
    }
    if (name === 'description' && value.length > 65) {
      return; // Ne pas mettre à jour si plus de 65 caractères
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Vérifier si l'URL est requise
  const isUrlRequired = formData.type === 'online' || formData.type === 'both';
  // Vérifier si l'URL doit être affichée
  const showUrlField = formData.type === 'online' || formData.type === 'both' || formData.type === 'announcement';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Upload image et création dans Firestore
      let imageUrl = '';
      if (imageFile) {
        const storage = getStorage();
        const timestamp = Date.now();
        const storageRef = ref(storage, `deals/${timestamp}_${imageFile.name}`);
        
        // Ajout des métadonnées pour le cache
        const metadata = {
          cacheControl: 'max-age=604800', // 7 jours en secondes
          contentType: imageFile.type || 'image/jpeg'
        };
        
        await uploadBytes(storageRef, imageFile, metadata);
        imageUrl = await getDownloadURL(storageRef);
      }

      // Préparation des données
      const db = getFirestore();
      const dealsRef = collection(db, 'deals');
      const snapshot = await getDocs(dealsRef);
      const batch = writeBatch(db);

      // Incrémenter l'ordre de tous les éléments existants
      snapshot.docs.forEach((doc) => {
        const docRef = doc.ref;
        batch.update(docRef, { order: doc.data().order + 1 });
      });

      const now = new Date();
      const dealData = {
        titre: formData.titre,
        description: formData.description,
        type: formData.type,
        imageUrl,
        timestamp: Timestamp.fromDate(now),
        dateCreation: now.toISOString(),
        order: 0,
      };

      // Ajout de l'URL selon les conditions
      if ((formData.type === 'online' || formData.type === 'both') || 
          (formData.type === 'announcement' && formData.urlRedirection)) {
        dealData.urlRedirection = formData.urlRedirection;
      }

      // Ajout des dates si activées
      if (hasDateLimit) {
        dealData.dateDebut = formData.dateDebut ? Timestamp.fromDate(new Date(formData.dateDebut)) : null;
        dealData.dateFin = formData.dateFin ? Timestamp.fromDate(new Date(formData.dateFin)) : null;
      }

      // Ajout des conditions si présentes
      if (formData.conditions) {
        dealData.conditions = formData.conditions;
      }

      // Créer le nouveau deal avec order: 0
      const newDealRef = doc(collection(db, 'deals'));
      await batch.set(newDealRef, dealData);

      // Exécuter toutes les mises à jour en une seule fois
      await batch.commit();

      // Envoi de la notification seulement si l'option est activée
      if (sendNotif) {
        await sendNotification({
          title: formData.titre,
          description: formData.description,
          imageUrl,
          url: `https://www.vaposmoketoday.fr/deals`,
          type: 'deal'
        });
      }

      window.dispatchEvent(new Event('dealCreated'));
      onSuccess();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de la création du bon plan');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      title="Nouveau bon plan"
      onClose={onClose}
      className="deal-form"
    >
      <div className="modal-sections">
        <form onSubmit={handleSubmit} className="form-section">
          {/* Section Informations */}
          <div className="form-group">
            <h3>Informations principales</h3>
            <Select
              label="Type de bon plan"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={typeOptions}
              required
            />
          </div>

          <div className="form-group">
            <Input
              label="Titre pour la notification Push"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              placeholder="Ex: -50% sur toute la gamme Alfaliquid (max 25 caractères)"
              required
            />
            <div className="character-count">
              {formData.titre.length}/25 caractères
            </div>
            <p className="field-info">
              Ce titre sera utilisé pour la notification push et servira également d'identifiant pour retrouver le bon plan dans la liste.
              <br />
              <span className="field-warning">Note : Ce titre n'est pas affiché dans l'application mobile.</span>
            </p>
          </div>

          <div className="form-group notification-option">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sendNotif}
                onChange={(e) => setSendNotif(e.target.checked)}
              />
              <span>Envoyer une notification push</span>
            </label>
            <p className="field-info">
              Si cette option est décochée, le bon plan sera publié mais aucune notification ne sera envoyée aux utilisateurs.
            </p>
          </div>

          <div className="form-group">
            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Décrivez votre bon plan... (max 65 caractères)"
              required={sendNotif}
              disabled={!sendNotif}
            />
            <div className="character-count">
              {formData.description.length}/65 caractères
            </div>
            <p className="field-info">
              Cette description sera utilisée uniquement pour la notification push.
              <br />
              <span className="field-warning">Note : Cette description n'est pas affichée dans l'application mobile.</span>
            </p>
          </div>

          {showUrlField && (
            <div className="form-group">
              <Input
                type="url"
                label={formData.type === 'announcement' ? "URL de redirection (optionnel)" : "URL de redirection"}
                name="urlRedirection"
                value={formData.urlRedirection}
                onChange={handleChange}
                placeholder="https://..."
                required={isUrlRequired}
              />
            </div>
          )}

          {/* Section Dates */}
          <div className="form-group">
            <h3>Période de validité</h3>
            <div className="date-section">
              <label>Limiter la période de validité</label>
              <DateCheckbox
                checked={hasDateLimit}
                onChange={(e) => setHasDateLimit(e.target.checked)}
              />
            </div>
            <p className="field-info">
              Si cette option est cochée, le bon plan aura une durée de validité définie et sera marqué comme tel dans l'application.
              <br />
              Si elle n'est pas cochée, le bon plan sera considéré comme valide indéfiniment.
              <br />
              <span className="field-warning">
                Important : Vous devrez supprimer manuellement le bon plan une fois sa date de validité atteinte.
              </span>
            </p>
          </div>

          {hasDateLimit && (
            <>
              <div className="form-group">
                <Input
                  type="datetime-local"
                  label="Date de début"
                  name="dateDebut"
                  value={formData.dateDebut}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <Input
                  type="datetime-local"
                  label="Date de fin"
                  name="dateFin"
                  value={formData.dateFin}
                  onChange={handleChange}
                  required
                />
              </div>
            </>
          )}

          {/* Section Conditions */}
          <div className="form-group">
            <h3>Conditions</h3>
            <TextArea
              label="Conditions particulières"
              name="conditions"
              value={formData.conditions}
              onChange={handleChange}
              placeholder="Conditions particulières (optionnel)"
            />
            <p className="field-info">
              Ces conditions seront affichées dans l'application mobile et permettront aux utilisateurs de connaître les détails spécifiques du bon plan.
              <br />
              Exemples : "Valable uniquement sur les produits en stock", "Non cumulable avec d'autres offres", etc.
            </p>
          </div>

          {/* Déplacer les boutons à la fin du formulaire */}
          <div className="form-actions">
            <Button 
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
              className="cancel-btn"
              type="button"
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isLoading}
              className="submit-btn"
            >
              Créer le bon plan
            </Button>
          </div>
        </form>

        {/* Section Image Upload */}
        <div className="image-section">
          <h3>Image du bon plan</h3>
          <div className="image-upload-zone">
            {!imagePreview ? (
              <>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="image-input"
                  id="image-input"
                  required
                />
                <label htmlFor="image-input" className="upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Glisser une image ou cliquer pour sélectionner</span>
                </label>
              </>
            ) : (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Prévisualisation" />
                <button 
                  type="button" 
                  className="remove-image"
                  onClick={handleImageRemove}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
          </div>
          <p className="image-info">Dimensions recommandées : 1080x1920 pixels pour un affichage optimal sur mobile</p>
        </div>
      </div>
    </Modal>
  );
};

export default CreateDealForm; 